/**
 * Municipal Data Context
 * Provides access to new municipal-level datasets (climate, emissions, air quality, etc.)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode
} from 'react';
import type {
  MunicipalRecord,
  MunicipalIndicatorMetadata,
  MunicipalDomainMetadata,
  MunicipalDataset,
  YearRange
} from '../utils/parquetDataLoader';
import { SOURCE_TO_BUNDLE, loadBootstrapDataset } from '../utils/parquetDataLoader';
import { loadMunicipalDataset } from '../utils/municipalDataClient';

// Context type definition
interface MunicipalDataContextType {
  data: MunicipalRecord[];
  isLoading: boolean;
  isPreloading: boolean;
  pendingIndicator: string | null;
  error: Error | null;
  indicators: Record<string, MunicipalIndicatorMetadata>;
  categories: Record<string, MunicipalDomainMetadata>;
  availableIndicators: string[];
  availableYears: number[];
  municipalities: string[];
  selectedIndicator: string | null;
  selectedYear: number | null;
  selectedMunicipality: string | null;
  setSelectedIndicator: (indicator: string | null) => void;
  setSelectedYear: (year: number | null) => void;
  setSelectedMunicipality: (municipality: string | null) => void;
  getFilteredData: () => MunicipalRecord[];
  getMunicipalityTimeSeries: (municipalityCode: string, indicator: string) => { year: number; value: number }[];
  getIndicatorMap: () => Map<string, number>;
  getIndicatorStatistics: () => {
    min: number;
    max: number;
    mean: number;
    median: number;
    stdDev: number;
  };
}

const MunicipalDataContext = createContext<MunicipalDataContextType | undefined>(undefined);

const CORE_COLUMNS = new Set(['PRO_COM', 'year']);
const DEFAULT_INDICATOR = 't';
const PRELOAD_INDICATORS = ['t', 'tmax', 'pm10'];

function deriveIndicatorKeys(
  metadata: Record<string, MunicipalIndicatorMetadata>,
  currentIndicator: string | null
): { indicators: string[]; defaultIndicator: string | null } {
  const indicatorKeys = Object.keys(metadata).sort((a, b) => {
    const orderA = metadata[a]?.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = metadata[b]?.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });
  const preferred = indicatorKeys.includes(DEFAULT_INDICATOR)
    ? DEFAULT_INDICATOR
    : indicatorKeys[0] ?? null;
  const next = currentIndicator && indicatorKeys.includes(currentIndicator)
    ? currentIndicator
    : preferred;
  return { indicators: indicatorKeys, defaultIndicator: next };
}

function deriveMunicipalities(data: MunicipalRecord[]): string[] {
  return Array.from(new Set(data.map((record) => record.PRO_COM).filter(Boolean))).sort();
}

function deriveYearsFromData(data: MunicipalRecord[]): number[] {
  return Array.from(
    new Set(
      data
        .map((record) => (typeof record.year === 'number' ? record.year : null))
        .filter((year): year is number => year !== null && !Number.isNaN(year))
    )
  ).sort((a, b) => a - b);
}

function deriveLoadedRange(data: MunicipalRecord[]): YearRange | null {
  const years = deriveYearsFromData(data);
  if (years.length === 0) {
    return null;
  }
  return { min: years[0], max: years[years.length - 1] };
}

function formatError(error: unknown): Error {
  const instance = error instanceof Error ? error : new Error('Failed to load municipal data');
  const message = instance.message;
  if (message.includes('11287024') || message.includes('municipalities.geojson')) {
    return new Error('Large map data is temporarily unavailable. Using provincial-level data instead.');
  }
  if (message.includes('fetch') || message.includes('network')) {
    return new Error(`Network error: ${message}`);
  }
  return instance;
}

export const MunicipalDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setData] = useState<MunicipalRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Changed to false - no initial load
  const [isInitialized, setIsInitialized] = useState(false); // Track if metadata is loaded
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Track if actual data is loaded
  const [error, setError] = useState<Error | null>(null);
  const [indicators, setIndicators] = useState<Record<string, MunicipalIndicatorMetadata>>({});
  const [categories, setCategories] = useState<Record<string, MunicipalDomainMetadata>>({});
  const [availableIndicators, setAvailableIndicators] = useState<string[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | null>(null);
  const [loadedYearRange, setLoadedYearRange] = useState<YearRange | null>(null);
  const [isPreloading, setIsPreloading] = useState(false);
  const [pendingIndicator, setPendingIndicator] = useState<string | null>(null);

  const selectedIndicatorRef = useRef<string | null>(null);
  const selectedYearRef = useRef<number | null>(null);
  const indicatorsRef = useRef<Record<string, MunicipalIndicatorMetadata>>({});
  const loadedColumnsRef = useRef<Record<string, string[]>>({});
  const loadedIndicatorsRef = useRef<Set<string>>(new Set());
  const datasetCacheRef = useRef<Map<string, MunicipalDataset>>(new Map());
  const inflightRef = useRef<Map<string, Promise<MunicipalDataset>>>(new Map());
  const preloadTriggeredRef = useRef(false);
  const preloadingCountRef = useRef(0);
  const preloadedYearsRef = useRef<Map<string, Set<number>>>(new Map());

  const updatePreloading = useCallback((isStarting: boolean) => {
    preloadingCountRef.current = Math.max(0, preloadingCountRef.current + (isStarting ? 1 : -1));
    setIsPreloading(preloadingCountRef.current > 0);
  }, []);

  useEffect(() => {
    selectedIndicatorRef.current = selectedIndicator;
  }, [selectedIndicator]);

  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  const buildColumnsPayload = useCallback((additionalIndicators: string[] = []) => {
    const columnsByBundle: Record<string, Set<string>> = {};

    Object.entries(loadedColumnsRef.current).forEach(([bundle, cols]) => {
      if (!cols || bundle === 'geo') {
        return;
      }
      const set = columnsByBundle[bundle] ?? new Set<string>();
      cols.forEach((col) => {
        if (col) {
          set.add(col);
        }
  });
      columnsByBundle[bundle] = set;
    });

    const requiredIndicators = new Set<string>(loadedIndicatorsRef.current);
    additionalIndicators.forEach((indicator) => {
      if (indicator) {
        requiredIndicators.add(indicator);
      }
    });

    if (requiredIndicators.size === 0) {
      requiredIndicators.add(selectedIndicatorRef.current ?? DEFAULT_INDICATOR);
    }

    requiredIndicators.forEach((indicator) => {
      const meta = indicatorsRef.current[indicator];
      if (!meta) {
        return;
      }
      const bundle = SOURCE_TO_BUNDLE[meta.sourceFile as keyof typeof SOURCE_TO_BUNDLE];
      if (!bundle) {
        return;
      }
      const set = columnsByBundle[bundle] ?? new Set<string>();
      set.add(indicator);
      columnsByBundle[bundle] = set;
    });

    const result: Record<string, string[]> = {};
    Object.entries(columnsByBundle).forEach(([bundle, set]) => {
      const columns = Array.from(set).sort();
      if (columns.length > 0) {
        result[bundle] = columns;
      }
    });

    return Object.keys(result).length > 0 ? result : undefined;
  }, []);

  const applyDataset = useCallback((dataset: MunicipalDataset, preserveIndicator = false) => {
    setData(dataset.data);
    setIndicators(dataset.indicators);
    setCategories(dataset.categories);
    indicatorsRef.current = dataset.indicators;

    const { indicators: indicatorKeys, defaultIndicator } = deriveIndicatorKeys(
      dataset.indicators,
      preserveIndicator ? selectedIndicatorRef.current : null
    );
    setAvailableIndicators(indicatorKeys);

    const yearOptions = dataset.allYears && dataset.allYears.length > 0
      ? dataset.allYears
      : deriveYearsFromData(dataset.data);
    setAvailableYears(yearOptions);

    setMunicipalities(deriveMunicipalities(dataset.data));

    const range = dataset.loadedYearRange ?? deriveLoadedRange(dataset.data);
    setLoadedYearRange(range);

    const loadedColumns = dataset.loadedColumns ?? {};
    loadedColumnsRef.current = loadedColumns;
    const newLoadedIndicators = new Set<string>();
    if (Object.keys(loadedColumns).length > 0) {
      Object.values(loadedColumns).forEach((columns) => {
        columns?.forEach((col) => {
          if (col && !CORE_COLUMNS.has(col)) {
            newLoadedIndicators.add(col);
          }
        });
      });
    } else {
      dataset.data.forEach((record) => {
        Object.keys(record).forEach((col) => {
          if (!CORE_COLUMNS.has(col) && record[col] !== undefined && record[col] !== null) {
            newLoadedIndicators.add(col);
          }
        });
      });
    }
    loadedIndicatorsRef.current = newLoadedIndicators;

    const nextIndicator = preserveIndicator && selectedIndicatorRef.current && indicatorKeys.includes(selectedIndicatorRef.current)
      ? selectedIndicatorRef.current
      : defaultIndicator;
    if (nextIndicator !== selectedIndicatorRef.current) {
      setSelectedIndicator(nextIndicator);
      selectedIndicatorRef.current = nextIndicator;
    }

    const currentYear = selectedYearRef.current;
    const withinRange = currentYear !== null && yearOptions.includes(currentYear);
    const nextYear = withinRange ? currentYear : (dataset.targetYear ?? yearOptions[yearOptions.length - 1] ?? null);
    if (nextYear !== currentYear) {
      setSelectedYear(nextYear);
      selectedYearRef.current = nextYear;
    }

    const indicatorKey = selectedIndicatorRef.current;
    const yearToTrack = selectedYearRef.current;
    if (indicatorKey && typeof yearToTrack === 'number') {
      const trackedYears = preloadedYearsRef.current.get(indicatorKey) ?? new Set<number>();
      if (!trackedYears.has(yearToTrack)) {
        trackedYears.add(yearToTrack);
        preloadedYearsRef.current.set(indicatorKey, trackedYears);
      }
    }
  }, []);

  const loadDataset = useCallback(async (
    targetYear?: number,
    preserveIndicator = false,
    resetOnError = false,
    additionalIndicators: string[] = [],
    lowPriority = false
  ) => {
    const columnsByBundle = buildColumnsPayload(additionalIndicators);
    const cacheKey = JSON.stringify({ year: targetYear ?? 'all', columns: columnsByBundle ?? null });

    const cached = datasetCacheRef.current.get(cacheKey);
    if (cached) {
      applyDataset(cached, preserveIndicator);
      return;
    }

    const inflight = inflightRef.current.get(cacheKey);
    if (inflight) {
      try {
        if (!lowPriority) {
          setIsLoading(true);
        }
        const result = await inflight;
        applyDataset(result, preserveIndicator);
      } catch (err) {
        const formatted = formatError(err);
        setError(formatted);
        if (resetOnError) {
          setData([]);
          setAvailableIndicators([]);
          setAvailableYears([]);
          setMunicipalities([]);
        }
      } finally {
        if (!lowPriority) {
          setIsLoading(false);
        }
      }
      return;
    }

    try {
      if (lowPriority) {
        updatePreloading(true);
      } else {
        setIsLoading(true);
      }
      if (!lowPriority && additionalIndicators.length > 0) {
        setPendingIndicator(additionalIndicators[0] ?? null);
      }
      setError(null);
      const promise = loadMunicipalDataset({ targetYear, columnsByBundle });
      inflightRef.current.set(cacheKey, promise);
      const result = await promise;
      inflightRef.current.delete(cacheKey);
      datasetCacheRef.current.set(cacheKey, result);
      applyDataset(result, preserveIndicator);
    } catch (err) {
      inflightRef.current.delete(cacheKey);
      const formatted = formatError(err);
      setError(formatted);
      if (resetOnError) {
        setData([]);
        setAvailableYears([]);
        setMunicipalities([]);
      }
    } finally {
      if (lowPriority) {
        updatePreloading(false);
      } else {
        setIsLoading(false);
      }
      if (additionalIndicators.length > 0 && !lowPriority) {
        setPendingIndicator(null);
      }
    }
  }, [applyDataset, buildColumnsPayload]);

  const prefetchDataset = useCallback(async (year: number, indicator: string): Promise<boolean> => {
    const columnsByBundle = buildColumnsPayload([indicator]);
    const cacheKey = JSON.stringify({ year, columns: columnsByBundle ?? null });

    if (datasetCacheRef.current.has(cacheKey)) {
      return true;
    }

    const inflight = inflightRef.current.get(cacheKey);
    if (inflight) {
      try {
        await inflight;
        return true;
      } catch (err) {
        console.warn('Prefetch inflight request failed, will retry later', err);
        return false;
      }
    }

    updatePreloading(true);
    let success = false;
    try {
      const promise = loadMunicipalDataset({ targetYear: year, columnsByBundle });
      inflightRef.current.set(cacheKey, promise);
      const result = await promise;
      datasetCacheRef.current.set(cacheKey, result);
      success = true;
    } catch (err) {
      console.warn('Failed to prefetch municipal dataset', { indicator, year, error: err });
    } finally {
      inflightRef.current.delete(cacheKey);
      updatePreloading(false);
    }
    return success;
  }, [buildColumnsPayload, updatePreloading]);

  // Load bootstrap data first, then full data on demand
  const loadBootstrap = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Loading bootstrap dataset...');

      // Load bootstrap dataset
      const bootstrap = await loadBootstrapDataset();
      console.log('Bootstrap loaded successfully:', bootstrap.data.length, 'records');

      // Apply bootstrap data
      setData(bootstrap.data);
      setIndicators(bootstrap.indicators);
      indicatorsRef.current = bootstrap.indicators;
      setCategories(bootstrap.categories);

      const { indicators: indicatorKeys, defaultIndicator } = deriveIndicatorKeys(bootstrap.indicators, null);
      setAvailableIndicators(indicatorKeys);
      setSelectedIndicator(defaultIndicator);

      // Set years from bootstrap
      const allYears = bootstrap.allYears || [2023];
      setAvailableYears(allYears);
      setSelectedYear(bootstrap.targetYear || 2023);

      // Derive municipalities from bootstrap data
      const munis = deriveMunicipalities(bootstrap.data);
      setMunicipalities(munis);

      setLoadedYearRange(bootstrap.loadedYearRange || null);
      loadedIndicatorsRef.current.add('t'); // Bootstrap contains temperature

      setIsInitialized(true);
      setIsDataLoaded(true); // Mark as having some data
      setError(null);

      console.log('Bootstrap initialization complete');

      // 🚀 OPTIMIZATION: Immediately preload ALL remaining years for temperature indicator
      // This ensures smooth year slider transitions without delays
      const initialIndicator = defaultIndicator || 't';
      const loadedYear = bootstrap.targetYear || 2023;
      const remainingYears = allYears.filter(y => y !== loadedYear);

      if (remainingYears.length > 0) {
        console.log(`🚀 Starting parallel preload of ${remainingYears.length} years for "${initialIndicator}" indicator`);

        // Mark current year as preloaded
        const trackedYears = new Set<number>([loadedYear]);
        preloadedYearsRef.current.set(initialIndicator, trackedYears);

        // Preload all remaining years in parallel (non-blocking)
        setTimeout(async () => {
          updatePreloading(true);
          try {
            const preloadPromises = remainingYears.map(async (year) => {
              try {
                const success = await prefetchDataset(year, initialIndicator);
                if (success) {
                  trackedYears.add(year);
                  console.log(`✅ Preloaded year ${year} for "${initialIndicator}"`);
                }
                return success;
              } catch (err) {
                console.warn(`⚠️ Failed to preload year ${year}:`, err);
                return false;
              }
            });

            const results = await Promise.all(preloadPromises);
            const successCount = results.filter(Boolean).length;
            console.log(`✅ Preloaded ${successCount}/${remainingYears.length} years for "${initialIndicator}" - Year slider now instant!`);

            // Update tracked years
            preloadedYearsRef.current.set(initialIndicator, trackedYears);
          } catch (err) {
            console.warn('⚠️ Parallel preload encountered error:', err);
          } finally {
            updatePreloading(false);
          }
        }, 0);
      }
    } catch (err) {
      console.warn('Bootstrap failed, falling back to metadata only:', err);
      // Instead of loading full data, just load metadata
      try {
        const response = await fetch('/data/metadata_new_datasets.json');
        if (response.ok) {
          const metadata = await response.json();
          // Convert Foglio2 data to indicators format
          if (metadata.Foglio2 && metadata.Foglio2.data) {
            const indicators: Record<string, any> = {};
            metadata.Foglio2.data.forEach((item: any) => {
              if (item['VAR CODE'] && item['VAR DES']) {
                indicators[item['VAR CODE']] = {
                  name: item['VAR DES'],
                  description: item['VAR DES'],
                  unit: '',
                  category: item['TOPIC'] || 'General',
                  timeInvariant: false,
                  sourceFile: item['FILE (.csv .txt)'] || 'unknown',
                  order: item['#'] || 999
                };
              }
            });
            setIndicators(indicators);
            indicatorsRef.current = indicators;
            const { indicators: indicatorKeys } = deriveIndicatorKeys(indicators, null);
            setAvailableIndicators(indicatorKeys);
          }
        }
        setAvailableYears([2020, 2021, 2022, 2023]);
        setSelectedYear(2023);
        setIsInitialized(true);
      } catch (metaErr) {
        console.error('Even metadata loading failed:', metaErr);
        setError(new Error('Failed to load application data'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [prefetchDataset, updatePreloading]);

  // Load bootstrap bundle on mount for fast initial render
  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  // Load full data when user selects something beyond bootstrap
  const loadFullDataIfNeeded = useCallback(async (indicator?: string | null, year?: number | null) => {
    // Check if we need full data (user selected something not in bootstrap)
    const needsFullData =
      (indicator && indicator !== 't') ||
      (year && year !== 2023) ||
      (!isDataLoaded && data.length < 100); // Bootstrap has ~244 provincial records

    if (needsFullData && !isLoading && isInitialized) {
      // Load full dataset for the selected year/indicator
      const targetYear = year ?? selectedYear ?? undefined;
      const additionalIndicators = indicator && indicator !== 't' ? [indicator] : [];

      await loadDataset(targetYear, true, false, additionalIndicators);
      setIsDataLoaded(true);
    }
  }, [isDataLoaded, isLoading, isInitialized, selectedYear, data.length, loadDataset]);

  useEffect(() => {
    if (selectedYear === null) {
      return;
    }
    if (loadedYearRange && selectedYear >= loadedYearRange.min && selectedYear <= loadedYearRange.max) {
      return;
    }
    loadDataset(selectedYear, true, false);
  }, [selectedYear, loadedYearRange, loadDataset]);

  useEffect(() => {
    if (!selectedIndicator) {
      return;
    }
    if (loadedIndicatorsRef.current.has(selectedIndicator)) {
      return;
    }
    loadDataset(selectedYearRef.current ?? undefined, true, false, [selectedIndicator]);
  }, [selectedIndicator, loadDataset]);

  useEffect(() => {
    if (!selectedIndicator || availableYears.length <= 1 || isLoading) {
      return;
    }

    const indicatorKey = selectedIndicator;
    const trackedYears = preloadedYearsRef.current.get(indicatorKey) ?? new Set<number>();

    const currentYear = selectedYearRef.current ?? selectedYear;
    if (typeof currentYear === 'number' && !trackedYears.has(currentYear)) {
      trackedYears.add(currentYear);
      preloadedYearsRef.current.set(indicatorKey, trackedYears);
    }

    const candidates = availableYears
      .filter((year) => {
        if (typeof currentYear === 'number' && year === currentYear) {
          return false;
        }
        return !trackedYears.has(year);
      })
      .sort((a, b) => {
        if (typeof currentYear !== 'number') {
          return a - b;
        }
        return Math.abs(a - currentYear) - Math.abs(b - currentYear);
      });

    if (candidates.length === 0) {
      return;
    }

    let cancelled = false;

    const runPrefetch = async () => {
      for (const year of candidates) {
        if (cancelled) {
          break;
        }
        const success = await prefetchDataset(year, indicatorKey);
        if (cancelled) {
          break;
        }
        if (success) {
          const updated = preloadedYearsRef.current.get(indicatorKey) ?? new Set<number>();
          updated.add(year);
          preloadedYearsRef.current.set(indicatorKey, updated);
        }
      }
    };

    runPrefetch();

    return () => {
      cancelled = true;
    };
  }, [selectedIndicator, availableYears, selectedYear, isLoading, prefetchDataset]);

  useEffect(() => {
    if (preloadTriggeredRef.current || data.length === 0) {
      return;
    }
    preloadTriggeredRef.current = true;

    const preload = () => {
      const indicatorsToLoad = PRELOAD_INDICATORS.filter((indicator) => !loadedIndicatorsRef.current.has(indicator));
      if (indicatorsToLoad.length === 0) {
        return;
      }
      const preloadPromise = loadDataset(selectedYearRef.current ?? undefined, true, false, indicatorsToLoad, true);
      if (preloadPromise && typeof preloadPromise.catch === 'function') {
        preloadPromise.catch(() => {});
      }
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(preload, { timeout: 2000 });
      } else {
        setTimeout(preload, 2000);
      }
    }
  }, [data.length, loadDataset]);

  const getFilteredData = useCallback(() => {
    if (!selectedIndicator) return [];
    return data.filter((record) => {
      if (selectedYear !== null && record.year !== undefined && record.year !== selectedYear) {
        return false;
      }
      const value = record[selectedIndicator];
      return value !== undefined && value !== null && value !== '';
    });
  }, [data, selectedIndicator, selectedYear]);

  const getMunicipalityTimeSeries = useCallback((municipalityCode: string, indicator: string) => {
    const municipalityData = data.filter((record) => record.PRO_COM === municipalityCode);
    const series: { year: number; value: number }[] = [];
    municipalityData.forEach((record) => {
      if (typeof record.year === 'number') {
        const value = record[indicator];
        if (typeof value === 'number') {
          series.push({ year: record.year, value });
        }
      }
    });
    return series.sort((a, b) => a.year - b.year);
  }, [data]);

  const getIndicatorMap = useCallback(() => {
    if (!selectedIndicator) return new Map<string, number>();
    const map = new Map<string, number>();
    data.forEach((record) => {
      if (selectedYear !== null && record.year !== undefined && record.year !== selectedYear) {
        return;
      }
      const value = record[selectedIndicator];
      if (typeof value === 'number') {
        map.set(record.PRO_COM, value);
      }
    });
    return map;
  }, [data, selectedIndicator, selectedYear]);

  const getIndicatorStatistics = useCallback(() => {
    if (!selectedIndicator) {
      return { min: 0, max: 0, mean: 0, median: 0, stdDev: 0 };
    }
    const values: number[] = [];
    data.forEach((record) => {
      if (selectedYear !== null && record.year !== undefined && record.year !== selectedYear) {
        return;
      }
      const value = record[selectedIndicator];
      if (typeof value === 'number' && !Number.isNaN(value)) {
        values.push(value);
      }
    });
    if (values.length === 0) {
      return { min: 0, max: 0, mean: 0, median: 0, stdDev: 0 };
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    return { min, max, mean, median, stdDev };
  }, [data, selectedIndicator, selectedYear]);

  // Wrapped setters that trigger data loading when needed
  const handleSetSelectedIndicator = useCallback((indicator: string | null) => {
    setSelectedIndicator(indicator);
    loadFullDataIfNeeded(indicator, selectedYear);
  }, [loadFullDataIfNeeded, selectedYear]);

  const handleSetSelectedYear = useCallback((year: number | null) => {
    setSelectedYear(year);
    loadFullDataIfNeeded(selectedIndicator, year);
  }, [loadFullDataIfNeeded, selectedIndicator]);

  const handleSetSelectedMunicipality = useCallback((municipality: string | null) => {
    setSelectedMunicipality(municipality);
    // No need to load full data just for municipality selection
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consuming components
  const value: MunicipalDataContextType = useMemo(() => ({
    data,
    isLoading: isLoading || (!isInitialized && !error), // Show loading if not initialized
    isPreloading,
    pendingIndicator,
    error,
    indicators,
    categories,
    availableIndicators,
    availableYears,
    municipalities,
    selectedIndicator,
    selectedYear,
    selectedMunicipality,
    setSelectedIndicator: handleSetSelectedIndicator,
    setSelectedYear: handleSetSelectedYear,
    setSelectedMunicipality: handleSetSelectedMunicipality,
    getFilteredData,
    getMunicipalityTimeSeries,
    getIndicatorMap,
    getIndicatorStatistics,
  }), [
    data,
    isLoading,
    isInitialized,
    isPreloading,
    pendingIndicator,
    error,
    indicators,
    categories,
    availableIndicators,
    availableYears,
    municipalities,
    selectedIndicator,
    selectedYear,
    selectedMunicipality,
    handleSetSelectedIndicator,
    handleSetSelectedYear,
    handleSetSelectedMunicipality,
    getFilteredData,
    getMunicipalityTimeSeries,
    getIndicatorMap,
    getIndicatorStatistics
  ]);

  return (
    <MunicipalDataContext.Provider value={value}>
      {children}
    </MunicipalDataContext.Provider>
  );
};

export const useMunicipalData = (): MunicipalDataContextType => {
  const context = useContext(MunicipalDataContext);
  if (context === undefined) {
    throw new Error('useMunicipalData must be used within a MunicipalDataProvider');
  }
  return context;
};

export default MunicipalDataContext;
